'use client';

import type { ReactNode } from 'react';
import {
  LIST_HEADER_FILL_STRONG,
  ListHeaderFillProvider,
  ListRowMarkPlacementProvider
} from '@/components/shared/list-row';

/**
 * **An AI review row draws its marks beside its text, not in a right-hand
 * column** (VALE-1737).
 *
 * One component so the reason is stated once and the set is greppable — the
 * same shape `review-empty-state.tsx` uses for the other half of this ticket.
 * Every review list wrapper renders this; `__tests__/ai-review-row-mark-placement-registry.test.ts`
 * reconciles the two lists and fails when a sixth wrapper appears without it.
 *
 * ## Why the review goes the other way from the rest of the app
 *
 * VALE-1725 moved row status pills into a dedicated right-aligned column
 * across the app — Contacts, Redemption, the Estate Account, Team members,
 * Discovery, the Entitlements page — and it was right to. On those surfaces a
 * pill is a **state being compared row to row**: which contacts are
 * identified, which orders are outstanding. A column is what makes that
 * comparison possible, and a mark parked mid-row defeats it.
 *
 * A review row is not that. It is a **proposal**, and its mark says something
 * about the proposal's own text — *this name is new*, *check this
 * institution*, *this clause is removed*. Read beside the thing it qualifies
 * it is part of the sentence; parked at the right edge it becomes a column of
 * pills the eye scans separately from the values the reviewer is actually
 * there to read. Nothing on a review is being compared down a column, because
 * every row is a different question.
 *
 * So this is one decision with two correct answers rather than a surface
 * lagging a standard, and it is a property of the **list**, never of the row:
 * `ContactRow` renders on the Contacts tab and on the review, and must draw
 * differently in each. That is why placement arrives by context — the same
 * reason `useAiReviewLayout()` derives a block's column count from the width
 * it is given rather than from a prop each host remembers to pass.
 *
 * ## It also declares the strip fill, for the same reason (VALE-1756)
 *
 * A section-header strip's fill used to arrive with the row **rhythm**, which
 * bundled two decisions that are not the same one. A rhythm is about row
 * height, and this review picks two of them on purpose: `roomy` for the
 * contacts, inventory and class-proposal lists, `compact` for documents and
 * entitlements, because a bulk extract puts thirty file rows above the values
 * the reviewer is here to check (VALE-1584). The strip's fill is about telling
 * one list from the next, which is a property of the stacked column they all
 * sit in — so the two dense lists were reading with a visibly lighter grey
 * above them than their three siblings, on a screen whose whole job is to be
 * scanned block by block.
 *
 * `LIST_HEADER_FILL_STRONG` is declared here rather than per list precisely so
 * an eighth review list cannot be added at the wrong grey: every review list
 * wrapper already renders this component, and the registry test below fails
 * when one appears that does not.
 *
 * ## What it does not change
 *
 * The marks themselves, their order, the `⋯` menu, the row heights each rhythm
 * sets, and every non-review surface. Placement defaults to `column` and the
 * fill defaults to the rhythm's own, so a list that says nothing keeps exactly
 * what VALE-1725 and VALE-1494 gave it.
 */
export function ReviewRowMarksInline({ children }: { children: ReactNode }) {
  return (
    <ListRowMarkPlacementProvider placement="inline">
      <ListHeaderFillProvider fill={LIST_HEADER_FILL_STRONG}>
        {children}
      </ListHeaderFillProvider>
    </ListRowMarkPlacementProvider>
  );
}
